import {
  type ConversionRates,
  convertCurrency,
  type OverviewMerchant,
  type OverviewTotalsCurrency,
  type OverviewValues,
} from "@unbox/api";

export type TotalsCurrency = OverviewTotalsCurrency;

export const TOTALS_CURRENCIES = ["GBP", "USD", "EUR"] as const satisfies readonly TotalsCurrency[];

export const FALLBACK_TOTALS_CURRENCY: TotalsCurrency = "GBP";

const TOTALS_CURRENCY_NAMES: Record<TotalsCurrency, string> = {
  GBP: "British pound",
  USD: "US dollar",
  EUR: "Euro",
};

const currencyNames = new Intl.DisplayNames(["en"], { type: "currency" });

export const isTotalsCurrency = (currency: string): currency is TotalsCurrency =>
  (TOTALS_CURRENCIES as readonly string[]).includes(currency);

export const currencyName = (currency: string): string =>
  isTotalsCurrency(currency) ? TOTALS_CURRENCY_NAMES[currency] : (currencyNames.of(currency) ?? currency);

export const currencySymbol = (currency: string): string =>
  new Intl.NumberFormat("en-US", { style: "currency", currency, currencyDisplay: "narrowSymbol" })
    .formatToParts(0)
    .find((part) => part.type === "currency")?.value ?? currency;

export const conversionFactor = (from: string, to: string, rates: ConversionRates | null): number | null =>
  convertCurrency(1, from, to, rates ?? undefined);

export const inCurrency = (values: OverviewValues, factor: number): OverviewValues => ({
  ...values,
  shopGmv: values.shopGmv * factor,
  affiliateGmv: values.affiliateGmv * factor,
});

export const defaultTotalsCurrency = (merchants: OverviewMerchant[], rates: ConversionRates | null): TotalsCurrency => {
  const tally = new Map<TotalsCurrency, { merchants: number; gmv: number }>();
  for (const merchant of merchants) {
    if (!isTotalsCurrency(merchant.currency)) continue;
    const entry = tally.get(merchant.currency) ?? { merchants: 0, gmv: 0 };
    entry.merchants += 1;
    entry.gmv += merchant.current.shopGmv * (conversionFactor(merchant.currency, "USD", rates) ?? 0);
    tally.set(merchant.currency, entry);
  }

  const ranked = [...tally.entries()].sort(([, a], [, b]) => b.merchants - a.merchants || b.gmv - a.gmv);
  return ranked[0]?.[0] ?? FALLBACK_TOTALS_CURRENCY;
};

export const localCurrencyOf = (regions: string[], scoped: OverviewMerchant[]): string | null => {
  if (regions.length === 0) return null;
  const currencies = new Set(scoped.map((merchant) => merchant.currency));
  return currencies.size === 1 ? [...currencies][0] : null;
};
